import { ANALYTICS_PAGE_KEYS } from '@/constants/state'
import { useTvlBreakdowns } from '@/hooks/analytics/useTvlBreakdowns'
import { useSearchState } from '@/hooks/useSearchState'
import { useVaultsQuery } from '@/queries/vaults.query'
import type { Vault } from '@/types/vault'
import { formatUSD } from '@vaultsfyi/common/utils'
import { Loader } from '../common/loader'
import { StatisticsTile } from '../common/statisticsTile'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../common/tabs'
import { TvlBreakdownChart } from './tvlBreakdownChart'
import { TvlBreakdownLegend } from './tvlBreakdownLegend'

export const TABS = [
  { key: 'network', label: 'Network' },
  { key: 'asset', label: 'Asset' },
  { key: 'protocol', label: 'Protocol' },
  { key: 'curator', label: 'Curator' },
] as const

interface Props {
  initialData?: Vault[] | null
}

export const TvlSection = ({ initialData }: Props) => {
  const { data: vaults, isFetching } = useVaultsQuery({ initialData: initialData ?? undefined })
  const breakdowns = useTvlBreakdowns(vaults)
  const { searchState, updateSearchState } = useSearchState({})
  const rawTab = searchState.get(ANALYTICS_PAGE_KEYS.TVL.key)
  const activeTab = (
    TABS.map((t) => t.key).includes(rawTab as never) ? rawTab : TABS[0].key
  ) as (typeof TABS)[number]['key']

  if (isFetching && !breakdowns) {
    return (
      <div className="flex w-full items-center justify-center py-16">
        <Loader />
      </div>
    )
  }

  if (!breakdowns) return null

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-3">
        <StatisticsTile label="Total TVL" value={formatUSD(breakdowns.total)} className="flex-grow" />
        <StatisticsTile label="Vaults" value={breakdowns.vaultCount.toLocaleString()} className="flex-grow" />
        <StatisticsTile label="Protocols" value={breakdowns.protocolCount} className="flex-grow" />
        <StatisticsTile label="Curators" value={breakdowns.curatorCount} className="flex-grow" />
      </div>

      <Tabs value={activeTab} onValueChange={(value) => updateSearchState([ANALYTICS_PAGE_KEYS.TVL.key, value])}>
        <div className="flex flex-col gap-2">
          <span className="text-body-foreground text-sm">TVL breakdown by</span>
          <TabsList className="w-full p-2">
            {TABS.map(({ key, label }) => (
              <TabsTrigger key={key} value={key} className="h-10 px-3 text-sm">
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {TABS.map(({ key }) => (
          <TabsContent key={key} value={key} className="mt-4">
            <div className="flex flex-wrap items-start justify-around gap-4">
              <TvlBreakdownLegend items={breakdowns[key]} className="max-w-96" />
              <TvlBreakdownChart items={breakdowns[key]} />
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
